import React, { useCallback } from "react"
import { Button, Flex } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { NewConfirmModal } from "ui/NewConfirmModal"
import useActiveGuide from "hooks/useActiveClient"
import BlackoutSectionCard from "./components/BlackoutSectionCard"
import BlackoutRow from "./components/BlackoutRow"
import ResourcesBlackoutCard from "./components/ResourcesBlackoutCard"
import BlackoutFormModal from "./modals/BlackoutFormModal"
import { useBlackouts } from "./hooks/useBlackouts"
import { BLACKOUT_SCOPES } from "./constants"
import styles from "./index.module.css"

export const BlackoutSettings = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isActive, isPending } = useActiveGuide()
  const isDisabled = !isActive || isPending

  const {
    byScope,
    isLoading,
    isSaving,
    isDeleting,
    formState,
    openAddModal,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete,
    getMenuItems,
    activeResourceTab,
    setActiveResourceTab
  } = useBlackouts({ enabled: true })

  // The dedicated conflict-review screen is not built yet; send the guide to the bookings
  // list so the warning is still actionable.
  const handleReviewConflicts = useCallback(() => navigate(routes.guideBookingsPath()), [navigate])

  const renderAddButton = (scope) => (
    <Button
      color="primary"
      variant="outlined"
      shape="round"
      icon={<PlusOutlined />}
      onClick={isDisabled ? undefined : () => openAddModal(scope)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
    >
      {t("settings.blackout.add", { defaultValue: "Add" })}
    </Button>
  )

  return (
    <>
      <Flex justify="flex-end" className={styles.pageActions}>
        <Button color="primary" variant="solid" shape="round" onClick={() => navigate(routes.proCalendarPath())}>
          {t("settings.blackout.view_in_calendar", { defaultValue: "View in Calendar" })}
        </Button>
      </Flex>

      <BlackoutSectionCard
        title={t("settings.blackout.business_title", { defaultValue: "Business Blackout" })}
        description={t("settings.blackout.business_description", { defaultValue: "Applies to all listings, crew and equipment" })}
        action={renderAddButton(BLACKOUT_SCOPES.BUSINESS)}
        isLoading={isLoading}
        isEmpty={!byScope[BLACKOUT_SCOPES.BUSINESS].length}
        emptyText={t("settings.blackout.empty_business", { defaultValue: "No business blackouts yet" })}
      >
        {byScope[BLACKOUT_SCOPES.BUSINESS].map((blackout) => (
          <BlackoutRow key={blackout.id} blackout={blackout} menuItems={getMenuItems(blackout)} onReviewConflicts={handleReviewConflicts} />
        ))}
      </BlackoutSectionCard>

      <div className={styles.columns}>
        <BlackoutSectionCard
          title={t("settings.blackout.listing_title", { defaultValue: "Listing Blackout" })}
          description={t("settings.blackout.listing_description", { defaultValue: "Selected listings are unavailable during this period" })}
          action={renderAddButton(BLACKOUT_SCOPES.LISTING)}
          isLoading={isLoading}
          isEmpty={!byScope[BLACKOUT_SCOPES.LISTING].length}
          emptyText={t("settings.blackout.empty_listing", { defaultValue: "No listing blackouts yet" })}
        >
          {byScope[BLACKOUT_SCOPES.LISTING].map((blackout) => (
            <BlackoutRow key={blackout.id} blackout={blackout} menuItems={getMenuItems(blackout)} onReviewConflicts={handleReviewConflicts} />
          ))}
        </BlackoutSectionCard>

        <ResourcesBlackoutCard
          crewBlackouts={byScope[BLACKOUT_SCOPES.CREW]}
          equipmentBlackouts={byScope[BLACKOUT_SCOPES.EQUIPMENT]}
          activeTab={activeResourceTab}
          onTabChange={setActiveResourceTab}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onAdd={openAddModal}
          getMenuItems={getMenuItems}
          onReviewConflicts={handleReviewConflicts}
        />
      </div>

      {formState && (
        <BlackoutFormModal
          isOpen
          scope={formState.scope}
          blackout={formState.blackout}
          onCancel={closeFormModal}
          onSubmit={handleSubmit}
          isSaving={isSaving}
        />
      )}

      <NewConfirmModal
        visible={!!blackoutToDelete}
        onClose={() => setBlackoutToDelete(null)}
        onConfirm={handleDelete}
        title={t("settings.blackout.delete_title", { defaultValue: "Delete Blackout?" })}
        message={t("settings.blackout.delete_message", { defaultValue: "Are you sure you want to delete this blackout?" })}
        confirmLabel={t("settings.action_delete", { defaultValue: "Delete" })}
        loading={isDeleting}
      />
    </>
  )
}

export default BlackoutSettings
